import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useFlightSelection } from './hooks/useFlightSelection';
import bookingService from '../../services/bookingService';
import Breadcrumb from './components/Breadcrumb';
import FlightCard from './components/FlightCard';
import FlightFilters from './components/FlightFilters';
import FlightFareSummary from './components/FlightFareSummary';
import OneWayFlightHeader from './components/OneWayFlightHeader';
import ReturnFlightTabs from './components/ReturnFlightTabs';
import MultiCitySegments from './components/MultiCitySegments';
import FlightSummaryTable from './components/FlightSummaryTable';

const FlightSelectionPage = () => {
  const location = useLocation();
  const {
    // State
    loading,
    selectedFlights,
    activeFlightTab,
    filters,
    flightType,
    mode,
    totalPassengers,
    searchData,
    searchError,
    multiCitySegments,
    activeSegmentIndex,
    departureAirport,
    arrivalAirport,
    enrichedSegments,
    paginationInfo,
    partialChange,
    isActiveLegLocked,
    getPreservedFlightForActiveLeg,
    handleContinueFromLockedLeg,

    // Functions
    getDisplayFlights,
    handleFlightSelect,
    handleFilterChange,
    handleResetFilters,
    handlePageChange,
    setActiveFlightTab,
    setActiveSegmentIndex,
    handleNext
  } = useFlightSelection();

  // --- NEW: RECOVERY/REVOCATION LOGIC ---
  useEffect(() => {
    const checkAndRevokeAbandonedLock = async () => {
      const token = sessionStorage.getItem('pending_booking_token');
      const timestamp = sessionStorage.getItem('pending_booking_time');

      if (token) {
        const timeDiff = Date.now() - parseInt(timestamp || '0');
        const isRecentlyLocked = timeDiff < 10 * 60 * 1000;

        if (isRecentlyLocked) {
          console.log('🧹 [Frontend] Found abandoned booking token on FlightSelectionPage, revoking:', token);
          try {
            await bookingService.revokeLock(token);
            console.log('✅ [Frontend] Abandoned seat lock revoked successfully');
          } catch (err) {
            console.warn('⚠️ [Frontend] Failed to revoke abandoned lock:', err);
          }
        }

        sessionStorage.removeItem('pending_booking_token');
        sessionStorage.removeItem('pending_booking_time');
      }
    };

    checkAndRevokeAbandonedLock();
  }, []);
  // ----------------------------------------

  // Server-side pagination mapping
  const allDisplayFlights = getDisplayFlights();
  const searchParams = new URLSearchParams(location.search);
  let currentPage = paginationInfo?.current_page || parseInt(searchParams.get('page') || '1');
  let totalPages = paginationInfo?.total_pages || 1;
  let currentFlights = allDisplayFlights;

  if (flightType === 'return' && activeFlightTab === 'return') {
    totalPages = paginationInfo?.return_total_pages || 1;
  } else   if (flightType === 'multi-city') {
    const ITEMS_PER_PAGE = 5;
    totalPages = Math.ceil(allDisplayFlights.length / ITEMS_PER_PAGE) || 1;
    currentFlights = allDisplayFlights.slice(
      (currentPage - 1) * ITEMS_PER_PAGE,
      currentPage * ITEMS_PER_PAGE
    );
  }

  const returnLockedTabs = [];
  if (partialChange.isPartialChange && flightType === 'return') {
    if (!partialChange.changingOutbound) returnLockedTabs.push('outbound');
    if (!partialChange.changingReturn) returnLockedTabs.push('return');
  }

  const activeLegLocked = isActiveLegLocked();
  const preservedFlight = activeLegLocked ? getPreservedFlightForActiveLeg() : null;

  const getNoResultsMessage = () => {
    switch (flightType) {
      case 'return': return 'No return flights available for the selected route and dates.';
      case 'multi-city': return 'No multi-city flights available for the selected routes and dates.';
      default: return 'No flights available for the selected route and date.';
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="bg-jet-gray-50 pt-[70px]">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-jet-yellow mx-auto mb-4"></div>
            <p className="text-jet-gray-600">
              {mode === 'details' ? 'Loading flight details...' : 'Searching for flights...'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#f5f5f5] pt-[70px]">
      {/* Breadcrumb Navigation */}
      {mode === 'details' && <Breadcrumb />}

      <main className="max-w-[1200px] mx-auto py-6 px-4">
        <div className="bg-white rounded-lg shadow-sm p-6 max-lg:p-4">
          <div className="flex gap-6 max-lg:flex-col max-lg:gap-4">
            {/* Sidebar - Filters or Fare Summary */}
            {mode === 'details' ? (
              <FlightFareSummary
                selectedFlights={selectedFlights}
                totalPassengers={totalPassengers}
                onNext={handleNext}
              />
            ) : (
              <FlightFilters
                filters={filters}
                onFilterChange={handleFilterChange}
                onReset={handleResetFilters}
              />
            )}

            {/* Main Content */}
            <div className="flex-1 min-w-0">

              {/* Return flight tabs */}
              {flightType === 'return' && mode === 'search' && (
                <ReturnFlightTabs
                  activeTab={activeFlightTab}
                  onTabChange={(tab) => {
                    if (returnLockedTabs.includes(tab)) return;
                    if (tab !== 'outbound') return;
                    setActiveFlightTab('outbound');
                    handlePageChange(1);
                  }}
                  searchData={searchData}
                  departureAirport={departureAirport}
                  arrivalAirport={arrivalAirport}
                  lockedTabs={returnLockedTabs}
                />
              )}

              {/* One-way flight header */}
              {flightType === 'one-way' && mode === 'search' && (
                <OneWayFlightHeader
                  from={searchData.from}
                  to={searchData.to}
                  departure={searchData.departure}
                  departureAirport={departureAirport}
                  arrivalAirport={arrivalAirport}
                />
              )}

              {/* Multi-city segments */}
              {flightType === 'multi-city' && mode === 'search' && (
                <MultiCitySegments
                  segments={multiCitySegments}
                  activeSegmentIndex={activeSegmentIndex}
                  onSegmentClick={(index) => {
                    if (partialChange.lockedSegmentIndices?.includes(index)) return;
                    setActiveSegmentIndex(index);
                    handlePageChange(1);
                  }}
                  enrichedSegments={enrichedSegments}
                  lockedSegmentIndices={partialChange.lockedSegmentIndices || []}
                />
              )}

              {/* Flight Summary Table for details mode */}
              {mode === 'details' && (
                <FlightSummaryTable selectedFlights={selectedFlights} />
              )}

              {/* Flight Results for search mode */}
              {mode === 'search' && (
                activeLegLocked && preservedFlight ? (
                  <div>
                    <FlightCard
                      flight={preservedFlight}
                      totalPassengers={totalPassengers}
                      readOnly
                      onContinue={handleContinueFromLockedLeg}
                    />
                  </div>
                ) : flightType === 'return' && activeFlightTab === 'return' && selectedFlights.length === 0 ? (
                  <div className="text-center py-12 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="text-[#1976d2] text-lg font-semibold mb-2">
                      📌 Please select your outbound flight first
                    </div>
                    <div className="text-[#666] text-sm">
                      Choose a flight from the outbound tab to see available return flights.
                    </div>
                  </div>
                ) : allDisplayFlights.length > 0 ? (
                  <div>
                    {currentFlights.map(flight => (
                      <FlightCard
                        key={flight.id}
                        flight={flight}
                        totalPassengers={totalPassengers}
                        onSelect={handleFlightSelect}
                      />
                    ))}

                    {totalPages > 1 && (
                      <div className="flex justify-center items-center gap-2 mt-8 mb-4">
                        <button
                          onClick={() => handlePageChange(Math.max(currentPage - 1, 1))}
                          disabled={currentPage === 1}
                          className="px-4 py-2 border border-[#E0E0E0] rounded-md text-sm font-medium text-[#333] bg-white hover:bg-[#F5F5F5] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          Previous
                        </button>
                        <span className="text-sm font-medium text-[#666] px-2">
                          Page {currentPage} of {totalPages}
                        </span>
                        <button
                          onClick={() => handlePageChange(Math.min(currentPage + 1, totalPages))}
                          disabled={currentPage === totalPages}
                          className="px-4 py-2 border border-[#E0E0E0] rounded-md text-sm font-medium text-[#333] bg-white hover:bg-[#F5F5F5] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          Next
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="text-[#666] text-base">
                      {searchError || getNoResultsMessage()}
                    </div>
                    <div className="text-[#999] text-sm mt-2">
                      Please try different dates or destinations.
                    </div>
                  </div>
                )
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default FlightSelectionPage;